import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, QrCode, Gift, User, LogOut, Store,
  Menu, X, ChevronRight, Home, Plus, Users, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const MENU_ITEMS = [
  { path: '/partner/dashboard', icon: LayoutDashboard, label: 'Dashboard', shortLabel: 'Inicio' },
  { path: '/partner/verificar', icon: QrCode, label: 'Verificar Código', shortLabel: 'Verificar', accent: true },
  { path: '/partner/beneficios', icon: Gift, label: 'Mis Beneficios', shortLabel: 'Beneficios' },
  { path: '/partner/clientes', icon: Users, label: 'Mis Clientes', shortLabel: 'Clientes' },
  { path: '/partner/perfil', icon: User, label: 'Mi Perfil', shortLabel: 'Perfil' },
];

// Logo del toro solo
const BRAVO_LOGO = 'https://customer-assets.emergentagent.com/job_rebill-payment-fix/artifacts/vodl6hhv_Blanco-Icono.png';

// Desktop Sidebar Item
const SidebarItem = memo(({ item, isActive, collapsed }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      data-testid={`partner-nav-${item.path.split('/').pop()}`}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative ${
        isActive 
          ? item.accent 
            ? 'bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 text-[#D4AF37] border-l-2 border-[#D4AF37]'
            : 'bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 text-[#D4AF37] border-l-2 border-[#D4AF37]' 
          : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
      }`}
    >
      <Icon size={20} className={isActive ? 'text-[#D4AF37]' : 'group-hover:text-white'} />
      {!collapsed && (
        <span className="font-medium text-sm">{item.label}</span>
      )}
      {collapsed && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-zinc-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
          {item.label}
        </div>
      )}
    </Link>
  );
});

// Mobile Bottom Nav Item
const MobileNavItem = memo(({ item, isActive }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      data-testid={`partner-mobile-nav-${item.path.split('/').pop()}`}
      className={`flex flex-col items-center justify-center py-2 px-1 transition-colors ${
        isActive ? 'text-[#D4AF37]' : 'text-zinc-500'
      }`}
    >
      <Icon size={22} strokeWidth={isActive ? 2.5 : 1.5} />
      <span className={`text-[10px] mt-1 ${isActive ? 'font-semibold' : 'font-medium'}`}>
        {item.shortLabel}
      </span>
    </Link>
  );
});

const PartnerLayout = ({ children, currentPage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [partner, setPartner] = useState(() => {
    const cached = localStorage.getItem('partner_data');
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(!partner);
  const hasLoadedRef = React.useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    
    const token = localStorage.getItem('partner_token');
    if (!token) {
      navigate('/admin/login', { replace: true });
      return;
    }
    
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    if (partner) {
      setLoading(false);
      refreshPartnerData();
    } else {
      fetchPartnerData();
    }
  }, []);

  const refreshPartnerData = useCallback(async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await api.get('/partners/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartner(response.data);
      localStorage.setItem('partner_data', JSON.stringify(response.data));
    } catch (error) {
      console.error('Background refresh error:', error);
    }
  }, []);

  const fetchPartnerData = useCallback(async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await api.get('/partners/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartner(response.data);
      localStorage.setItem('partner_data', JSON.stringify(response.data));
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('partner_token');
      localStorage.removeItem('partner_data');
      navigate('/admin/login', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('partner_token');
    localStorage.removeItem('partner_data');
    toast.success('Sesión cerrada');
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const handleBackToAccount = useCallback(() => {
    navigate('/mi-cuenta');
  }, [navigate]);

  const isActive = useCallback((path) => {
    // Handle /partner/dashboard as root
    if (path === '/partner/dashboard' && location.pathname === '/partner/dashboard') {
      return true;
    }
    // Handle other paths
    if (path !== '/partner/dashboard' && location.pathname.startsWith(path)) {
      return true;
    }
    return location.pathname === path;
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-500 text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex flex-col fixed left-0 top-0 h-full bg-zinc-950 border-r border-zinc-800/50 transition-all duration-300 z-40 ${
          sidebarCollapsed ? 'w-[72px]' : 'w-60'
        }`}
      >
        {/* Logo Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800/50">
          <Link to="/" className="flex items-center gap-2">
            <img src={BRAVO_LOGO} alt="Bravo" className="h-8 w-8 object-contain" />
            {!sidebarCollapsed && (
              <span className="font-bold text-white text-sm">PARTNERS</span>
            )}
          </Link>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ChevronRight size={16} className={`transition-transform ${sidebarCollapsed ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* Partner Card */}
        <div className={`p-3 border-b border-zinc-800/50 ${sidebarCollapsed ? 'px-2' : ''}`}>
          <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'justify-center' : ''}`}>
            {partner?.logo_url ? (
              <img 
                src={partner.logo_url} 
                alt={partner.business_name}
                className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-9 h-9 bg-gradient-to-br from-[#D4AF37]/30 to-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Store className="text-[#D4AF37]" size={18} />
              </div>
            )}
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm truncate">{partner?.business_name}</p>
                <span className="text-[10px] text-zinc-500">@{partner?.username}</span>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {MENU_ITEMS.map((item) => (
            <SidebarItem
              key={item.path}
              item={item}
              isActive={isActive(item.path)}
              collapsed={sidebarCollapsed}
            />
          ))}
        </nav>

        {/* Create Benefit Button */}
        {!sidebarCollapsed && (
          <div className="p-3 border-t border-zinc-800/50">
            <Link
              to="/partner/crear-beneficio"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black font-semibold text-sm rounded-lg hover:opacity-90 transition-opacity"
            >
              <Plus size={18} />
              Nuevo Beneficio
            </Link>
          </div>
        )}

        {/* Back to Home & Logout */}
        <div className={`p-3 border-t border-zinc-800/50 space-y-1 ${sidebarCollapsed ? 'px-2' : ''}`}>
          <Link
            to="/"
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-400 hover:bg-zinc-800/50 hover:text-white transition-colors w-full ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}
          >
            <Home size={18} />
            {!sidebarCollapsed && <span className="text-sm font-medium">Ir a Bravo</span>}
          </Link>
          <button
            onClick={handleBackToAccount}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-400 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors w-full ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}
          >
            <ArrowLeft size={18} />
            {!sidebarCollapsed && <span className="text-sm font-medium">Mi Cuenta</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-zinc-950/95 backdrop-blur-sm border-t border-zinc-800/50 flex items-center justify-around px-2 z-40 safe-area-bottom">
        {MENU_ITEMS.map((item) => (
          <MobileNavItem
            key={item.path}
            item={item}
            isActive={isActive(item.path)}
          />
        ))}
      </nav>

      {/* Mobile Slide-out Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-72 bg-zinc-950 z-50 flex flex-col"
            >
              {/* Mobile Menu Header */}
              <div className="h-14 flex items-center justify-between px-4 border-b border-zinc-800/50">
                <div className="flex items-center gap-2">
                  <img src={BRAVO_LOGO} alt="Bravo" className="h-7 w-7 object-contain" />
                  <span className="font-bold text-white text-sm">PARTNERS</span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 -mr-2 text-zinc-400 hover:text-white"
                >
                  <X size={22} />
                </button>
              </div>
              
              {/* Partner Card */}
              <div className="p-4 border-b border-zinc-800/50">
                <div className="flex items-center gap-3">
                  {partner?.logo_url ? (
                    <img 
                      src={partner.logo_url} 
                      alt={partner.business_name}
                      className="w-12 h-12 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37]/30 to-[#D4AF37]/10 rounded-xl flex items-center justify-center">
                      <Store className="text-[#D4AF37]" size={24} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white truncate">{partner?.business_name}</p>
                    <p className="text-xs text-zinc-500 truncate">@{partner?.username}</p>
                  </div>
                </div>
              </div>
              
              {/* Create Benefit Button */}
              <div className="p-4">
                <Link
                  to="/partner/crear-beneficio"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black font-bold rounded-xl"
                >
                  <Plus size={20} />
                  Nuevo Beneficio
                </Link>
              </div>
              
              {/* Navigation */}
              <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
                {MENU_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                        active 
                          ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                          : 'text-zinc-400 hover:bg-zinc-800'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
              
              {/* Back to Bravo */}
              <div className="p-3 border-t border-zinc-800/50">
                <Link
                  to="/"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:bg-zinc-800 transition-colors"
                >
                  <Home size={20} />
                  <span className="font-medium">Volver a Bravo</span>
                </Link>
              </div>
              
              {/* Back to Account */}
              <div className="p-3 border-t border-zinc-800/50">
                <button
                  onClick={handleBackToAccount}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors w-full"
                >
                  <ArrowLeft size={20} />
                  <span className="font-medium">Mi Cuenta</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className={`min-h-screen pt-4 pb-20 lg:pt-0 lg:pb-0 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-60'
      }`}>
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default PartnerLayout;
